import { downloadPublicMedia } from '../../lib/mediaUrls';
import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, ListMusic, Volume2, VolumeX, Repeat, Repeat1, Shuffle, ChevronRight, Loader, Download, Music } from 'lucide-react';
import { RepeatMode, ShuffleMode, Song } from './types';
import { formatTime } from './playerUtils';
import { AnimatePresence, motion } from 'framer-motion';

interface ControlsProps {
  isPlaying: boolean;
  isLoading: boolean;
  repeatMode: RepeatMode;
  shuffleMode: ShuffleMode;
  isMuted: boolean;
  handlePlayPause: () => void;
  playPreviousSong: () => void;
  playNextSong: () => void;
  toggleRepeatMode: () => void;
  toggleShuffleMode: () => void;
  toggleMute: () => void;
  toggleVolumeControl: () => void;
  currentSong?: Song | null;
}

interface ProgressBarProps {
  progress: number;
  currentTime: number;
  duration: number;
  progressRef: React.RefObject<HTMLDivElement>;
  handleProgressClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleProgressMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  formatTime: (time: number) => string;
  currentAudioTime: number;
}

export const DesktopControls: React.FC<ControlsProps> = ({
  isPlaying,
  isLoading,
  handlePlayPause,
  playPreviousSong,
  playNextSong,
  currentSong
}) => {
  const handleDownload = () => downloadPublicMedia(currentSong?.songUrl);
  
  return (
    <div className="flex items-center justify-center w-full">
      <div className="flex items-center bottom-[30.5px] justify-center space-x-6 absolute left-1/2 transform -translate-x-1/2">
        <button 
          onClick={playPreviousSong}
          className="text-gray-400 hover:text-white transition-colors"
          disabled={isLoading}
        >
          <SkipBack size={32} />
        </button>
        <button
          className={`w-14 h-14 bg-white flex items-center justify-center text-black hover:bg-gray-200 transition-colors ${
            isLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={handlePlayPause}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader size={32} className="animate-spin" />
          ) : isPlaying ? (
            <Pause size={32} />
          ) : (
            <Play size={32} />
          )}
        </button>
        <button 
          onClick={playNextSong}
          className="text-gray-400 hover:text-white transition-colors"
          disabled={isLoading}
        >
          <SkipForward size={32} />
        </button>
      </div>
    </div>
  );
};

export const ProgressBar: React.FC<{
  progress: number;
  currentTime: number;
  duration: number;
  progressRef: React.RefObject<HTMLDivElement>;
  handleProgressClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleProgressMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  formatTime: (time: number) => string;
  currentAudioTime: number;
}> = ({
  progress,
  currentTime,
  duration,
  progressRef,
  handleProgressClick,
  handleProgressMouseDown,
  formatTime,
  currentAudioTime,
}) => {
  // Handle progress value to ensure it's always a valid number between 0-100
  const safeProgress = isNaN(progress) || !isFinite(progress) ? 0 : Math.max(0, Math.min(100, progress));
  
  // console.log(`ProgressBar rendered: progress=${progress}, safeProgress=${safeProgress}, currentTime=${currentTime}`);
  
  return (
    <div className="w-full pb-0 pt-0">
      <div 
        ref={progressRef}
        className="relative h-4 bg-zinc-800 cursor-pointer progress-bar rounded-sm"
        onClick={(e) => {
          // console.log("Progress bar clicked in component");
          // Ensure the event doesn't continue to parent elements
          e.stopPropagation();
          handleProgressClick(e);
        }}
        onMouseDown={(e) => {
          // console.log("Progress bar mouse down in component");
          // Ensure the event doesn't continue to parent elements
          e.stopPropagation();
          handleProgressMouseDown(e);
        }}
        onTouchStart={(e) => {
          // console.log("Progress bar touch start");
          // For touch events, we need to extract the first touch point
          const touch = e.touches[0];
          const mouseEvent = new MouseEvent('mousedown', {
            clientX: touch.clientX,
            clientY: touch.clientY,
            bubbles: true,
            cancelable: true,
            view: window
          });
          
          e.currentTarget.dispatchEvent(mouseEvent);
          e.stopPropagation();
          e.preventDefault(); // Prevent scrolling during touch
        }}
        style={{ touchAction: 'none' }} // Prevent browser-handled touch actions
      >
        <div 
          className="absolute h-full bg-white"
          style={{ width: `${safeProgress}%` }}
        >
          <div 
            className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow-md" 
            style={{ borderRadius: '0' }}
            onMouseDown={(e) => {
              // Capture clicks on the handle as well
              e.stopPropagation();
              handleProgressMouseDown(e);
            }}
            onTouchStart={(e) => {
              // Handle touch on the slider handle
              // console.log("Handle touch start");
              const touch = e.touches[0];
              const mouseEvent = new MouseEvent('mousedown', {
                clientX: touch.clientX,
                clientY: touch.clientY,
                bubbles: true,
                cancelable: true,
                view: window
              });
              
              progressRef.current?.dispatchEvent(mouseEvent);
              e.stopPropagation();
              e.preventDefault();
            }}
          />
        </div>
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-400 px-1">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(currentAudioTime || duration)}</span>
      </div>
    </div>
  );
};

export const MobileControls: React.FC<ControlsProps> = ({
  isPlaying,
  isLoading,
  handlePlayPause,
  playPreviousSong,
  playNextSong,
  currentSong
}) => {
  const handleDownload = () => downloadPublicMedia(currentSong?.songUrl);
  
  return (
    <div className="flex items-center justify-center w-full mb-4 mobile-controls">
      <div className="flex items-center justify-center space-x-6">
        
        <button 
          onClick={playPreviousSong}
          className="text-gray-200 hover:text-white transition-colors p-3 touch-target"
          disabled={isLoading}
        >
          <SkipBack size={36} className="w-9 h-9" />
        </button>
        <button
          className={`play-button-mobile bg-white flex items-center justify-center text-black hover:bg-gray-200 transition-colors ${
            isLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={handlePlayPause}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader size={40} className="animate-spin" />
          ) : isPlaying ? (
            <Pause size={40} />
          ) : (
            <Play size={40} className="ml-1" />
          )}
        </button>
        <button 
          onClick={playNextSong}
          className="text-gray-200 hover:text-white transition-colors p-3 touch-target max-[420px]:p-2"
          disabled={isLoading}
        >
          <SkipForward size={36} className="w-9 h-9" />
        </button>
      </div>
    </div>
  );
};

interface BottomControlsProps extends ControlsProps {
  playlistButtonRef: React.RefObject<HTMLButtonElement>;
  showPlaylist: boolean;
  setShowPlaylist: (show: boolean) => void;
  setShowAlbums: React.Dispatch<React.SetStateAction<boolean>>;
  showVolumeControl: boolean;
  volume: number;
}

export const BottomControls: React.FC<BottomControlsProps> = ({
  repeatMode,
  shuffleMode,
  isMuted,
  toggleRepeatMode,
  toggleShuffleMode,
  toggleMute,
  toggleVolumeControl,
  playlistButtonRef,
  showPlaylist,
  setShowPlaylist,
  setShowAlbums,
  showVolumeControl,
  volume,
  currentSong
}) => {
  // Add state to track volume slider dragging
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);
  const volumeSliderRef = useRef<HTMLDivElement>(null);

  // Add handleDownload function for the download button
  const handleDownload = () => downloadPublicMedia(currentSong?.songUrl);

  // Handle volume slider drag events
  useEffect(() => {
    if (!isDraggingVolume || !volumeSliderRef.current) return;
    
    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!volumeSliderRef.current) return;
      
      // Prevent default to avoid scrolling during touch
      e.preventDefault();
      
      // Get client position for both mouse and touch events
      const clientY = 'touches' in e 
        ? e.touches[0].clientY 
        : e.clientY;
      
      // Calculate position relative to container
      const rect = volumeSliderRef.current.getBoundingClientRect();
      const y = clientY - rect.top;
      const containerHeight = rect.height;
      
      // Convert to volume (0-1) - inverted because 0 is at the bottom, 1 at the top
      const newVolume = 1 - Math.max(0, Math.min(1, y / containerHeight));
      
      // Tell parent component about volume change
      const event = new CustomEvent('volumeChange', {
        detail: { volume: newVolume }
      });
      window.dispatchEvent(event);
    };
    
    const handleMouseUp = () => {
      setIsDraggingVolume(false);
    };
    
    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchend', handleMouseUp);
    
    // Clean up
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDraggingVolume]);

  // Get shuffle indicator text
  const getShuffleIndicator = () => {
    switch (shuffleMode) {
      case 'album': return <span className="absolute -top-1 -right-1 text-[10px] bg-white text-black w-4 h-4 flex items-center justify-center">A</span>;
      case 'all': return <span className="absolute -top-1 -right-1 text-[10px] bg-white text-black w-4 h-4 flex items-center justify-center">*</span>;
      default: return null;
    }
  };

  return (
    <div className="flex items-center justify-center space-x-8 max-[420px]:space-x-4 w-full mobile-extra-controls">
      <div className="relative">
        <button 
          onClick={(e) => {
            e.stopPropagation(); // Prevent other click handlers
            toggleVolumeControl();
          }} 
          className="text-gray-300 hover:text-white transition-colors touch-target relative volume-toggle-btn"
        >
          {isMuted ? 
            <VolumeX size={28} strokeWidth={2} className="w-7 h-7" /> : 
            <Volume2 size={28} strokeWidth={2} className="w-7 h-7" />
          }
        </button>

        {/* Mobile Volume Control Popup */}
        <AnimatePresence>
          {showVolumeControl && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              transition={{ duration: 0.2 }}
              className="absolute flex left-[-2px] border-radius-0 flex-col justify-center items-center bottom-full mb-2 left-1/2 -translate-x-1/2 bg-black p-3 shadow-lg z-20 volume-control-popup"
              onClick={(e) => {
                // Critical: Prevent event from bubbling up to parent elements
                e.stopPropagation();
              }}
            >
              <div className="flex items-center mb-2">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMute();
                  }}
                  className="text-white mute-toggle-btn touch-target"
                >
                  {isMuted ? 
                    <VolumeX size={25} strokeWidth={2} style={{ color: 'red' }}/> : 
                    <Volume2 size={25} strokeWidth={2} style={{ color: 'gray' }}/>
                  }
                </button>
              </div>
              <div 
                ref={volumeSliderRef}
                className="h-24 w-3 bg-white relative cursor-pointer progress-bar volume-slider-track-responsive volume-slider-track"
                onClick={(e) => {
                  // Handle direct click to set volume immediately
                  e.stopPropagation();
                  
                  // Calculate position relative to container height
                  const rect = e.currentTarget.getBoundingClientRect();
                  const clickY = e.clientY - rect.top;
                  const containerHeight = rect.height;
                  
                  // Convert to volume (0-1) - inverted because 0 is at the bottom, 1 at the top
                  const newVolume = 1 - (clickY / containerHeight);
                  // Clamp value between 0 and 1
                  const clampedVolume = Math.max(0, Math.min(1, newVolume));
                  
                  // Tell parent component about volume change
                  const event = new CustomEvent('volumeChange', {
                    detail: { volume: clampedVolume }
                  });
                  window.dispatchEvent(event);
                  
                  // Keep the volume control open after making a selection
                  // Don't auto-close it
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setIsDraggingVolume(true);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setIsDraggingVolume(true);
                }}
              >
                {/* Slider knob - also handle dragging from the knob itself */}
                <div 
                  className="absolute w-3 h-2.5 bg-red-500"
                  style={{ 
                    top: `${(1 - (isMuted ? 0 : volume)) * 100}%`,
                    left: 0,
                    right: 0
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setIsDraggingVolume(true);
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    setIsDraggingVolume(true);
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      <div className="mx-1 relative touch-target max-[420px]:mx-0">
        <button 
          onClick={toggleRepeatMode}
          className="transition-colors tooltip-container"
          title={
            repeatMode === 'none' 
              ? 'Repeat: Off' 
              : repeatMode === 'album' 
                ? 'Repeat: Album' 
                : 'Repeat: Song'
          }
        >
          {repeatMode === 'none' ? (
            <Repeat size={28} strokeWidth={2} className="w-7 h-7 text-gray-500" />
          ) : repeatMode === 'album' ? (
            <Repeat size={28} strokeWidth={2} className="w-7 h-7 text-white" />
          ) : (
            <Repeat1 size={28} strokeWidth={2} className="w-7 h-7 text-white" />
          )}
        </button>
      </div>
      
      <button 
        onClick={toggleShuffleMode}
        className="transition-colors tooltip-container relative touch-target"
        title={
          shuffleMode === 'none' 
            ? 'Shuffle: Off' 
            : shuffleMode === 'album' 
              ? 'Shuffle: Album' 
              : 'Shuffle: All Songs'
        }
      >
        {shuffleMode === 'none' ? (
          <Shuffle size={28} strokeWidth={2} className="w-7 h-7 text-gray-500" />
        ) : (
          <Shuffle size={28} strokeWidth={2} className="w-7 h-7 text-white" />
        )}
        {getShuffleIndicator()}
      </button>
      
      {/* Album button */}
      <button
        onClick={(e) => {
          e.stopPropagation(); // Prevent event bubbling
          setShowAlbums(prev => !prev);
          setShowPlaylist(false);
        }}
        className="text-gray-300 hover:text-white transition-colors touch-target max-[420px]:mx-0"
        title="Select Album"
      >
        <Music className="h-7 w-7 max-[420px]:h-6 max-[420px]:w-6" />
      </button>
      
      {/* Playlist button */}
      <button
        ref={playlistButtonRef}
        onClick={(e) => {
          e.stopPropagation(); // Prevent event bubbling
          setShowPlaylist(!showPlaylist);
          setShowAlbums(false);
        }}
        className="text-gray-300 hover:text-white transition-colors touch-target max-[420px]:mx-0"
        title="Show Playlist"
      >
        <ListMusic size={28} strokeWidth={2} className="w-7 h-7 max-[420px]:w-6 max-[420px]:h-6" />
      </button>
      
      {currentSong?.downloadable && (
          <button
            onClick={handleDownload}
            className="text-gray-300 hover:text-white transition-colors touch-target max-[420px]:mx-0"
            title="Download song"
          >
            <Download size={28} className="w-7 h-7 max-[420px]:w-6 max-[420px]:h-6" />
          </button>
        )}

    </div>
  );
}; 